<template>
  <div class="flex flex-col px-4">
    <div class="grid grid-cols-3">
      <div class="flex flex-col col-span-2">
        <strong>{{ $t('settings.user') }}</strong>
        <span>{{ currentUser }}</span>
      </div>
    </div>
    <divider />
    <div class="grid grid-cols-3">
      <div class="flex flex-col col-span-2">
        <strong>{{ $t('settings.notifications.title') }}</strong>
        <span>{{ $t('settings.notifications.help') }}</span>
      </div>
      <div class="flex flex-row-reverse items-center">
        <toggle-switch v-model="pushFlag" :disabled="fetchError || loading" />
      </div>
      <div class="flex flex-col col-span-3" v-if="fetchError">
        <message severity="error" variant="simple">
          <template #default>
            <small>{{ $t('settings.notifications.error.fetch.line1') }}</small>
          </template>
        </message>
        <message severity="error" variant="simple">
          <template #default>
            <small>{{ $t('settings.notifications.error.fetch.line2') }}</small>
          </template>
        </message>
      </div>
      <div class="flex flex-col col-span-3">
        <message severity="warn" variant="simple" v-if="showMissingPermissionsWarning">
          <template #default>
            <small>{{ $t('settings.notifications.warning.noBrowserPermissions') }}</small>
          </template>
        </message>
      </div>
      <div class="flex flex-col col-span-3" v-if="showChangeError">
        <message severity="error" variant="simple">
          <template #default>
            <small>{{ $t('settings.notifications.error.change.line1') }}</small>
          </template>
        </message>
        <message severity="error" variant="simple">
          <template #default>
            <small>{{ $t('settings.notifications.error.change.line2') }}</small>
          </template>
        </message>
      </div>
    </div>
    <divider />
    <div class="grid grid-cols-3">
      <div class="flex flex-col col-span-2">
        <strong>{{ $t('settings.version') }}</strong>
        <span>{{ release }}</span>
      </div>
    </div>
    <divider />
  </div>
</template>

<script setup lang="ts">
import useLoading from '@/composables/useLoading';
import sentryConfig from '@/config/sentry';
import { useAppApi } from '@/plugins/appApi';
import { useKeycloak } from '@dsb-norge/vue-keycloak-js';
import { computed, onMounted, ref, watch, type Ref } from 'vue';

const loading = ref(false);
const pushFlag = ref(false);
const showChangeError = ref(false);
const showMissingPermissionsWarning = ref(false);
const fetchError = ref(false);

const currentUser = computed(() => {
  const kc = useKeycloak();
  return kc.userName;
});

const release = computed(() => {
  let version = sentryConfig.release ? sentryConfig.release.substring(0, 12) : '/';

  if (import.meta.env.DEV) {
    version += '-dev';
  }

  if (import.meta.env.PROD) {
    version += '-prd';
  }

  return version;
});

const fetchSettings = async () => {
  const client = useAppApi();

  try {
    let { data: subscriptions, error } = await client.GET('/api/subscriptions');

    if (error) {
      fetchError.value = true;
      return;
    }

    if (subscriptions) {
      pushFlag.value = subscriptions.pushEnabled;

      const suppress = ref(false);
      watch(pushFlag, async (newValue) => {
        if (suppress.value) {
          suppress.value = false;
          return;
        }

        await tryEnableNotifications(suppress, newValue);
      });
    }
  } catch (e) {
    console.error(e);
    fetchError.value = true;
    return;
  }
};

onMounted(() => useLoading(loading, fetchSettings));

const tryEnableNotifications = async (suppress: Ref<boolean, boolean>, newValue: boolean) => {
  showMissingPermissionsWarning.value = false;
  showChangeError.value = false;

  const gotPermissions = await requestNotificationPermissions();

  if (!gotPermissions) {
    suppress.value = true;
    pushFlag.value = false;
    showMissingPermissionsWarning.value = true;
    return;
  }

  const changed = newValue ? await enableNotifications() : await disableNotifications();

  if (!changed) {
    suppress.value = true;
    pushFlag.value = !newValue;
    showChangeError.value = true;
  }
};

const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.error('Service worker not in navigator!');
    return;
  }

  console.log('About to register worker...');
  const registration = await navigator.serviceWorker.register(
    import.meta.env.MODE === 'production' ? '/sw.js' : '/dev-sw.js?dev-sw',
    {
      type: 'module'
    }
  );
  console.log('Registered worker', registration);
};

const requestNotificationPermissions = async () => {
  const result = await Notification.requestPermission();
  if (result !== 'granted') {
    console.error('Notification permissions denied by user!');
    return false;
  }

  return true;
};

const enableNotifications = async () => {
  await registerServiceWorker();

  const serviceWorkerRegistration = await navigator.serviceWorker.ready;

  let pushManager = serviceWorkerRegistration.pushManager;

  return trySubscribe(pushManager);
};

const trySubscribe = async (pushManager: PushManager) => {
  try {
    const client = useAppApi();
    const { data: vapid } = await client.GET('/api/subscriptions/vapid');

    if (!vapid) {
      console.error('Failed to fetch VAPID key!');
      return false;
    }

    let subscription = await pushManager.getSubscription();
    let auth = '';
    let p256dh = '';
    if (subscription) {
      const json = subscription.toJSON();
      console.log('Existing subscription...', json);
      auth = json.keys!['auth'];
      p256dh = json.keys!['p256dh'];
    } else {
      console.log('About to create new subscription...');
      subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapid
      });
      const json = subscription.toJSON();
      console.log('New subscription...', json);
      auth = json.keys!['auth'];
      p256dh = json.keys!['p256dh'];
    }
    const { error } = await client.POST('/api/subscriptions', {
      body: {
        endpoint: subscription.endpoint,
        keys: {
          auth,
          p256dh
        }
      }
    });

    return !error;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const disableNotifications = async () => {
  await registerServiceWorker();

  const serviceWorkerRegistration = await navigator.serviceWorker.ready;

  let pushManager = serviceWorkerRegistration.pushManager;

  let subscription = await pushManager.getSubscription();

  if (!subscription) {
    console.error('Attempted to unsubscribe, but no subscription existed on device!');
    return;
  }

  if (subscription) {
    await subscription.unsubscribe();
    console.log('Unsubscribed device from push notifications.');
  }

  const client = useAppApi();
  try {
    const { response } = await client.POST('/api/unsubscribe', {
      body: {
        endpoint: subscription.endpoint
      }
    });
    return response.status === 200;
  } catch {
    return false;
  }
};
</script>
